import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useAuthModal } from '@/features/auth'
import { LANDING_VIDEOS } from '@/config/assets'

interface FeatureMiniCardProps {
  text: string
}

function FeatureMiniCard({ text }: FeatureMiniCardProps) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-white px-4 py-3 shadow-sm">
      <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-success">
        <Check className="h-3 w-3 text-white" />
      </div>
      <span className="text-sm font-medium text-foreground">{text}</span>
    </div>
  )
}

/**
 * NicolAI spotlight section with demo video
 * Implementation: LAND2-003
 */
export function NicolAISection() {
  const { t } = useTranslation()
  const { openAuthModal } = useAuthModal()

  const features = [
    t('landing.nicolai.feature1'),
    t('landing.nicolai.feature2'),
    t('landing.nicolai.feature3'),
    t('landing.nicolai.feature4'),
  ]

  return (
    <div
      className="relative py-16 sm:py-24 lg:py-32"
      style={{
        background: `radial-gradient(ellipse at 50% 10%, rgba(249, 115, 22, 0.12) 0%, rgba(249, 115, 22, 0.04) 40%, #FFFFFF 100%)`,
      }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Avatar with Halo */}
        <div className="mb-8 flex justify-center">
          <div
            className="flex items-center justify-center rounded-full"
            style={{
              width: 220,
              height: 220,
              background: `radial-gradient(circle at center, rgba(249, 115, 22, 0.35) 0%, rgba(249, 115, 22, 0.12) 40%, rgba(249, 115, 22, 0) 100%)`,
            }}
          >
            <div className="flex h-[150px] w-[150px] items-center justify-center rounded-full bg-primary shadow-xl">
              <span className="text-6xl">🤖</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <h2 className="mx-auto max-w-3xl text-center text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
          {t('landing.nicolai.title')}
        </h2>

        {/* Description */}
        <p className="mx-auto mt-6 max-w-2xl whitespace-pre-line text-center text-lg text-foreground-muted">
          {t('landing.nicolai.description')}
        </p>

        {/* Features Grid */}
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          {features.map((feature, index) => (
            <FeatureMiniCard key={index} text={feature} />
          ))}
        </div>

        {/* Demo Video */}
        <div className="mx-auto mt-12 max-w-4xl">
          <div className="aspect-[2/1] overflow-hidden rounded-2xl bg-background-secondary shadow-2xl">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="h-full w-full object-cover"
            >
              <source src={LANDING_VIDEOS.nicolaiDemo} type="video/webm" />
            </video>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 flex justify-center">
          <button
            onClick={() => openAuthModal()}
            className={cn(
              'rounded-lg bg-accent px-8 py-4 text-lg font-semibold text-white',
              'transition-all duration-200 ease-out',
              'hover:-translate-y-0.5 hover:bg-accent-hover hover:shadow-lg hover:shadow-accent/30',
              'focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-2'
            )}
          >
            {t('landing.nicolai.cta')}
          </button>
        </div>
      </div>
    </div>
  )
}
